import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  X,
  Search,
  FileText,
  Layers,
  Users,
  MessageSquare,
  Briefcase,
  Building2,
  LifeBuoy,
  ClipboardList,
  Loader2,
  Trash2,
  LayoutGrid,
} from 'lucide-react';

/**
 * Category icon mapping — maps backend category strings to Lucide icons
 */
const CATEGORY_ICONS = {
  'Lead Generation': Users,
  'Feedback': MessageSquare,
  'HR': Briefcase,
  'Client Onboarding': Building2,
  'Support': LifeBuoy,
  'Registration': ClipboardList,
};

function getCategoryIcon(category) {
  return CATEGORY_ICONS[category] || FileText;
}

/**
 * A small badge showing how many fields a template has
 */
function FieldCountBadge({ count }) {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
      {count} field{count !== 1 ? 's' : ''}
    </span>
  );
}

/**
 * A small badge showing the step count for multi-step forms
 */
function StepBadge({ steps }) {
  if (!steps || steps <= 1) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/30 px-1.5 py-0.5 rounded">
      {steps} steps
    </span>
  );
}

/**
 * Individual template card inside the gallery grid
 */
function TemplateCard({ template, isSelected, onSelect, isCustom, onDelete }) {
  const stepCount = template.fields?.steps?.length || 0;
  const Icon = getCategoryIcon(template.category);

  return (
    <button
      type="button"
      onClick={() => onSelect(template)}
      className={`
        text-left w-full rounded-lg border transition-all duration-150 p-4
        group relative
        ${isSelected
          ? 'border-brand-500 ring-2 ring-brand-500/20 bg-brand-50/50 dark:bg-brand-950/30 dark:border-brand-400'
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm bg-white dark:bg-gray-900'
        }
      `}
    >
      {/* Thumbnail area — simplified field preview */}
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isSelected
            ? 'bg-brand-100 dark:bg-brand-900/50'
            : 'bg-gray-100 dark:bg-gray-800 group-hover:bg-gray-200 dark:group-hover:bg-gray-700'
        } transition-colors`}>
          <Icon className={`w-4.5 h-4.5 ${isSelected ? 'text-brand-600 dark:text-brand-400' : 'text-gray-500 dark:text-gray-400'}`} />
        </div>
        <div className="flex items-center gap-1.5">
          <StepBadge steps={stepCount} />
          <FieldCountBadge count={template.field_count} />
        </div>
      </div>

      <h4 className={`text-sm font-semibold leading-tight ${
        isSelected ? 'text-brand-700 dark:text-brand-300' : 'text-gray-900 dark:text-gray-100'
      }`}>
        {template.name}
      </h4>

      {template.description && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 line-clamp-2 leading-relaxed">
          {template.description}
        </p>
      )}

      {/* Category tag */}
      <div className="mt-3">
        <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
          {template.category}
        </span>
      </div>

      {/* Delete button for custom templates */}
      {isCustom && onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(template.id);
          }}
          className="absolute top-2 right-2 p-1 rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100 transition-all"
          title="Delete template"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </button>
  );
}

/**
 * FormTemplateGallery — Modal for browsing, searching, and selecting form templates
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onSelectTemplate: (template) => Promise<void> | void — called with the selected template; the consumer is responsible for applying it
 * - templates: array — pre-fetched from useFormTemplates
 * - categories: array — pre-fetched from useFormTemplates
 * - loading: boolean
 * - onDeleteTemplate: (templateId) => void — for custom template deletion
 */
export default function FormTemplateGallery({
  isOpen,
  onClose,
  onSelectTemplate,
  templates = [],
  categories = [],
  loading = false,
  onDeleteTemplate,
}) {
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('system'); // 'system' | 'custom'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [applying, setApplying] = useState(false);
  const modalRef = useRef(null);
  const searchRef = useRef(null);

  // Focus search when modal opens
  useEffect(() => {
    if (isOpen && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
    // Reset state when opening
    if (isOpen) {
      setActiveCategory('all');
      setActiveTab('system');
      setSearchQuery('');
      setSelectedTemplate(null);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Filter templates
  const systemTemplates = useMemo(
    () => templates.filter(t => t.is_system),
    [templates]
  );
  const customTemplates = useMemo(
    () => templates.filter(t => !t.is_system),
    [templates]
  );

  const currentList = activeTab === 'system' ? systemTemplates : customTemplates;

  const filteredTemplates = useMemo(() => {
    let result = currentList;
    if (activeCategory !== 'all') {
      result = result.filter(t => t.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t =>
        t.name.toLowerCase().includes(q) ||
        (t.description && t.description.toLowerCase().includes(q)) ||
        t.category.toLowerCase().includes(q)
      );
    }
    return result;
  }, [currentList, activeCategory, searchQuery]);

  // Categories for the current tab
  const availableCategories = useMemo(() => {
    const cats = new Set(currentList.map(t => t.category));
    return Array.from(cats).sort();
  }, [currentList]);

  const handleApply = useCallback(async () => {
    if (!selectedTemplate || applying) return;
    setApplying(true);
    try {
      await onSelectTemplate(selectedTemplate);
    } finally {
      setApplying(false);
    }
  }, [selectedTemplate, applying, onSelectTemplate]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Template Gallery</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Choose a template to get started quickly
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab bar + Search */}
        <div className="px-6 pt-3 pb-0 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900">
          <div className="flex items-center justify-between gap-4 mb-3">
            {/* Tabs */}
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-0.5 rounded-lg">
              <button
                onClick={() => { setActiveTab('system'); setActiveCategory('all'); setSelectedTemplate(null); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  activeTab === 'system'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5" />
                  Templates
                </span>
              </button>
              <button
                onClick={() => { setActiveTab('custom'); setActiveCategory('all'); setSelectedTemplate(null); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  activeTab === 'custom'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <LayoutGrid className="w-3.5 h-3.5" />
                  My Templates
                  {customTemplates.length > 0 && (
                    <span className="ml-1 bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 text-[10px] px-1.5 py-0.5 rounded-full">
                      {customTemplates.length}
                    </span>
                  )}
                </span>
              </button>
            </div>

            {/* Search */}
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-colors"
              />
            </div>
          </div>

          {/* Category pills */}
          {availableCategories.length > 0 && (
            <div className="flex items-center gap-1.5 pb-3 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => { setActiveCategory('all'); setSelectedTemplate(null); }}
                className={`flex-shrink-0 px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                  activeCategory === 'all'
                    ? 'bg-brand-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                All
              </button>
              {availableCategories.map(cat => (
                <button
                  key={cat}
                  onClick={() => { setActiveCategory(cat); setSelectedTemplate(null); }}
                  className={`flex-shrink-0 px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    activeCategory === cat
                      ? 'bg-brand-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Template grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading templates...</p>
            </div>
          ) : filteredTemplates.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredTemplates.map(template => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  isSelected={selectedTemplate?.id === template.id}
                  onSelect={setSelectedTemplate}
                  isCustom={!template.is_system}
                  onDelete={onDeleteTemplate}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
                <FileText className="w-5 h-5 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {activeTab === 'custom'
                  ? 'No custom templates yet'
                  : 'No templates match your search'}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {activeTab === 'custom'
                  ? 'Save a form as a template from the form builder to see it here.'
                  : 'Try a different category or search term.'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900">
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={!selectedTemplate || applying}
              className={`
                px-4 py-2 text-sm font-medium rounded-lg transition-colors inline-flex items-center gap-2
                ${selectedTemplate && !applying
                  ? 'bg-brand-600 hover:bg-brand-700 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }
              `}
            >
              {applying && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {applying ? 'Creating...' : 'Use Template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
